export interface EmoteDefinition {
    id?: string;
    url?: string;
    url2x?: string;
    type?: string;
    animated?: boolean;
    emoji?: string;
}

export type EmoteDictionary = Record<string, EmoteDefinition>;
export type TwitchEmoteTags = Record<string, readonly string[]> | string | null | undefined;

export type ChatMessageToken =
    | { kind: 'text'; value: string }
    | { kind: 'link'; value: string; href: string }
    | { kind: 'emote'; name: string; url: string; url2x?: string; type: string; animated: boolean };

interface MessageRange {
    start: number;
    end: number;
    id: string;
    kind: 'twitch' | 'gif';
    url?: string;
}

const RANGE_PATTERN = /^(\d+)-(\d+)$/;
const SAFE_TYPE_PATTERN = /[^a-z0-9_-]/gi;
const TWITCH_EMOTE_ID_PATTERN = /^\d+$/;
const TRAILING_URL_PUNCTUATION = /[),.!?;:]+$/;

function parseRanges(id: string, ranges: readonly string[], kind: MessageRange['kind'], url?: string): MessageRange[] {
    return ranges.flatMap((range) => {
        const match = RANGE_PATTERN.exec(range);
        if (!match) return [];

        const start = Number(match[1]);
        const end = Number(match[2]);
        if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end < start) return [];
        return [{ start, end, id, kind, url }];
    });
}

function parseTwitchEmoteRanges(tags: TwitchEmoteTags): MessageRange[] {
    if (!tags) return [];

    if (typeof tags === 'string') {
        return tags.split('/').flatMap((entry) => {
            const separator = entry.indexOf(':');
            if (separator < 1) return [];
            return parseRanges(entry.slice(0, separator), entry.slice(separator + 1).split(','), 'twitch');
        });
    }

    return Object.entries(tags).flatMap(([id, ranges]) => parseRanges(id, ranges, 'twitch'));
}

function parseGifRanges(tags: string | null | undefined): MessageRange[] {
    if (!tags) return [];

    return tags.split(',').flatMap((entry) => {
        const [range, id, url] = entry.split('|');
        if (!range || !id || !url || !isSafeHttpUrl(url)) return [];
        return parseRanges(id, [range], 'gif', url);
    });
}

function isSafeHttpUrl(value: string): boolean {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

function appendText(tokens: ChatMessageToken[], value: string): void {
    if (!value) return;
    const previous = tokens.at(-1);
    if (previous?.kind === 'text') {
        previous.value += value;
    } else {
        tokens.push({ kind: 'text', value });
    }
}

function appendTextOrLink(tokens: ChatMessageToken[], value: string): void {
    const trailing = value.match(TRAILING_URL_PUNCTUATION)?.[0] ?? '';
    const candidate = trailing ? value.slice(0, -trailing.length) : value;

    if (isSafeHttpUrl(candidate)) {
        tokens.push({ kind: 'link', value: candidate, href: candidate });
        appendText(tokens, trailing);
    } else {
        appendText(tokens, value);
    }
}

function tokenizePlainText(value: string, emotes: EmoteDictionary, tokens: ChatMessageToken[]): void {
    const parts = value.match(/\s+|\S+/gu) ?? [];

    parts.forEach((part) => {
        if (/^\s+$/u.test(part)) {
            appendText(tokens, part);
            return;
        }

        const emote = emotes[part];
        if (emote?.url && !emote.type?.startsWith('twitch')) {
            tokens.push({
                kind: 'emote',
                name: part,
                url: emote.url,
                url2x: emote.url2x,
                type: (emote.type || 'third-party').replace(SAFE_TYPE_PATTERN, ''),
                animated: Boolean(emote.animated),
            });
            return;
        }

        if (emote?.emoji) {
            appendText(tokens, emote.emoji);
            return;
        }

        appendTextOrLink(tokens, part);
    });
}

function twitchEmoteDefinition(
    id: string,
    name: string,
    emotes: EmoteDictionary,
    emotesById: ReadonlyMap<string, EmoteDefinition>,
): EmoteDefinition {
    const exactDefinition = emotes[name];
    if (exactDefinition?.id === id && exactDefinition.url) return exactDefinition;

    const definitionById = emotesById.get(id);
    if (definitionById) return definitionById;

    const safeId = TWITCH_EMOTE_ID_PATTERN.test(id) ? id : encodeURIComponent(id);
    return {
        url: `https://static-cdn.jtvnw.net/emoticons/v2/${safeId}/default/dark/1.0`,
        url2x: `https://static-cdn.jtvnw.net/emoticons/v2/${safeId}/default/dark/2.0`,
        type: 'twitch',
    };
}

export function parseChatMessage(
    message: string,
    emotes: EmoteDictionary,
    twitchEmoteTags?: TwitchEmoteTags,
    gifTags?: string | null,
): ChatMessageToken[] {
    const codePoints = Array.from(message);
    const emotesById = new Map(
        Object.values(emotes)
            .filter((emote): emote is EmoteDefinition & { id: string; url: string } => Boolean(emote.id && emote.url))
            .map((emote) => [emote.id, emote]),
    );
    const ranges = [...parseTwitchEmoteRanges(twitchEmoteTags), ...parseGifRanges(gifTags)]
        .filter((range) => range.end < codePoints.length)
        .sort((left, right) => left.start - right.start || left.end - right.end);

    const tokens: ChatMessageToken[] = [];
    let cursor = 0;

    ranges.forEach((range) => {
        if (range.start < cursor) return;
        tokenizePlainText(codePoints.slice(cursor, range.start).join(''), emotes, tokens);

        const name = codePoints.slice(range.start, range.end + 1).join('');
        if (range.kind === 'gif' && range.url) {
            tokens.push({ kind: 'emote', name, url: range.url, type: 'twitch-gif', animated: true });
        } else {
            const definition = twitchEmoteDefinition(range.id, name, emotes, emotesById);
            tokens.push({
                kind: 'emote',
                name,
                url: definition.url!,
                url2x: definition.url2x,
                type: (definition.type || 'twitch').replace(SAFE_TYPE_PATTERN, ''),
                animated: Boolean(definition.animated),
            });
        }
        cursor = range.end + 1;
    });

    tokenizePlainText(codePoints.slice(cursor).join(''), emotes, tokens);
    return tokens;
}

export function renderChatMessage(target: HTMLElement, tokens: readonly ChatMessageToken[]): void {
    const fragment = document.createDocumentFragment();

    tokens.forEach((token) => {
        if (token.kind === 'text') {
            fragment.appendChild(document.createTextNode(token.value));
            return;
        }

        if (token.kind === 'link') {
            const link = document.createElement('a');
            link.href = token.href;
            link.textContent = token.value;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            fragment.appendChild(link);
            return;
        }

        const image = document.createElement('img');
        image.className = `emote emote-${token.type}`;
        image.src = token.url;
        if (token.url2x) image.srcset = `${token.url} 1x, ${token.url2x} 2x`;
        image.alt = token.name;
        image.title = token.name;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        if (token.animated) image.dataset.animated = 'true';
        image.addEventListener('error', () => image.replaceWith(document.createTextNode(token.name)), { once: true });
        fragment.appendChild(image);
    });

    target.replaceChildren(fragment);
}
